import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQuery, gql } from '@apollo/client';
import { DataExportJustificationDialog } from '../components/shared/DataExportJustificationDialog';
import { useAuth } from '../hooks/useAuth';

const HAS_ACTIVE_GDPR_JUSTIFICATION = gql`
  query HasActiveGdprJustification {
    hasActiveGdprJustification
  }
`;

interface GdprContextType {
  /** Whether user has active GDPR access (justified within TTL) */
  hasAccess: boolean;
  /** Call to request GDPR access — opens the justification dialog */
  requestAccess: () => void;
  /** Re-lock: immediately revoke local access for this page session */
  lock: () => void;
  /** The justification text (if provided in this session) */
  justification: string | null;
  /** Whether the server-side check is loading */
  loading: boolean;
}

const GdprContext = createContext<GdprContextType | undefined>(undefined);

export function useGdpr(): GdprContextType {
  const ctx = useContext(GdprContext);
  if (!ctx) {
    throw new Error('useGdpr must be used within a GdprProvider');
  }
  return ctx;
}

export const GdprProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [hasLocalAccess, setHasLocalAccess] = useState(false);
  const [manuallyLocked, setManuallyLocked] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [justification, setJustification] = useState<string | null>(null);

  // Server-side TTL check — re-fetches on every page load (network-only)
  const { data, loading } = useQuery(HAS_ACTIVE_GDPR_JUSTIFICATION, {
    fetchPolicy: 'network-only',
    skip: !isAuthenticated,
  });

  // If server says TTL is active and user hasn't manually locked, grant access
  useEffect(() => {
    if (!loading && data?.hasActiveGdprJustification === true && !manuallyLocked) {
      setHasLocalAccess(true);
    }
  }, [data, loading, manuallyLocked]);

  const hasAccess = !manuallyLocked && (hasLocalAccess || (data?.hasActiveGdprJustification === true));

  const requestAccess = useCallback(() => {
    if (hasAccess) return;
    setShowDialog(true);
  }, [hasAccess]);

  const lock = useCallback(() => {
    setManuallyLocked(true);
    setHasLocalAccess(false);
    setJustification(null);
  }, []);

  const handleConfirm = useCallback((j: string) => {
    setJustification(j);
    setHasLocalAccess(true);
    setManuallyLocked(false);
    setShowDialog(false);
  }, []);

  const handleCancel = useCallback(() => {
    setShowDialog(false);
  }, []);

  return (
    <GdprContext.Provider value={{ hasAccess, requestAccess, lock, justification, loading }}>
      {children}
      <DataExportJustificationDialog
        isOpen={showDialog}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        exportType="analytics-pii-access"
        columns={['userId', 'userName']}
        recordCount={-1}
        confirmLabel="Reveal Identities"
      />
    </GdprContext.Provider>
  );
};
